import type { AxiosError } from 'axios';
import type { SWRInfiniteConfiguration } from 'swr/infinite';
import useSWRInfinite from 'swr/infinite';

import { SpGroupType } from '@/utils/hooks/swr/group/useSPGroups';

import { get } from '@/utils/client';

import type { SocialProfileType } from '@/types/socialProfileType';
import type { AlbumType } from '@/lib/albums/types';

import { ALBUMS } from '@/routes/apiEndpoints';

export type AlbumsDataType = {
    data: AlbumType[];
    next_cursor: string;
};

type UseAlbumsProps = {
    activeSPID?: SocialProfileType['id'];
    activeGroupID?: SpGroupType['id'];
    limit: number;
    params?: {
        name?: string;
        /**
         * The type of album to filter by (Stories)
         */
        album_type?: string;
        /**
         * The type of album to filter by (Manual)
         */
        type?: string;
    };
    options?: SWRInfiniteConfiguration<AlbumsDataType, AxiosError<Error>>;
    cancelToken?: AbortController;
    shouldFetch?: boolean;
    includeGroupAlbums?: boolean;
};

const getKey = (
    pageIndex: number,
    previousPageData: AlbumsDataType,
    activeSPID: SocialProfileType['id'],
    activeGroupID: SpGroupType['id'],
    limit: number,
    params: UseAlbumsProps['params'],
    includeGroupAlbums?: boolean
) => {
    // Reached the end
    if (previousPageData && !previousPageData.data) return null;

    // Use new unified /albums endpoint

    let baseUrl = ALBUMS;

    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('per_page', limit.toString());

    // Add context-specific ID parameter
    if (activeGroupID) {
        queryParams.append('group_id', activeGroupID.toString());
    } else if (activeSPID) {
        queryParams.append('social_profile_id', activeSPID.toString());
    }

    if (includeGroupAlbums) {
        queryParams.append('only_group_albums', 'true');
    }

    if (params?.name) {
        queryParams.append('name', params.name);
    }

    if (params?.album_type) {
        queryParams.append('album_type', params.album_type);
    }

    if (params?.type) {
        queryParams.append('type', params.type);
    }

    // First page, we don't have `previousPageData`
    const url = `${baseUrl}?${queryParams.toString()}`;
    if (pageIndex === 0) return [url, JSON.stringify(params)];

    // Add the cursor to the API endpoint
    return [`${url}&cursor=${previousPageData.next_cursor}`, JSON.stringify(params)];
};

const useAlbums = ({
    activeSPID,
    activeGroupID,
    limit = 20,
    params = {},
    options = {},
    cancelToken,
    shouldFetch = true,
    includeGroupAlbums
}: UseAlbumsProps) => {
    const {
        data: albums,
        size,
        setSize,
        isLoading,
        mutate: mutateAlbums,
        error: albumsError
    } = useSWRInfinite<AlbumsDataType, AxiosError<Error>>(
        (pageIndex, previousPageData) =>
            shouldFetch
                ? getKey(
                      pageIndex,
                      previousPageData,
                      activeSPID,
                      activeGroupID,
                      limit,
                      params,
                      includeGroupAlbums
                  )
                : null,
        ([url]: string[]) =>
            get(url, params, { signal: cancelToken?.signal }).then(
                (res: { data: AlbumsDataType }) => res.data
            ),
        {
            shouldRetryOnError: false,
            ...options
        }
    );

    const isLoadingMore = size > 0 && albums && typeof albums[size - 1] === 'undefined';

    return {
        albums,
        mutateAlbums,
        albumsError,
        loadMoreAlbums: () => setSize(size + 1), // Function to load more albums (load more button)
        isLoadingInitialData: isLoading, // Check if it's the initial loading (not the load more)
        isLoadMore: albums && typeof albums[albums.length - 1]?.next_cursor === 'string', // Check if there is a cursor to active the load more button
        isLoadingMore // Check if the load more is loading
    };
};
export default useAlbums;
